import { createTheme, alpha } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { guidedCourseLight, guidedCourseDark } from './guidedCourse';
import '@mui/x-date-pickers/themeAugmentation';

const breakpoints = {
  values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
};

const lightPaletteOptions: NonNullable<ThemeOptions['palette']> = {
  mode: 'light',
  background: { default: '#EEF1F5', paper: '#FFF' },
  divider: '#D7D8DB',
  primary: { main: '#25507B', dark: '#234F7D' },
  secondary: { main: '#3572B0' },
  text: { primary: '#263238', secondary: '#7B7B7B' },
  error: { main: 'rgb(250, 50, 82)' },
  warning: { main: '#F29339' },
  info: { main: '#21A9DE' },
  success: { main: '#388E3C' },
  action: {
    selected: alpha('#25507B', 0.1),
  },
  guidedCourse: guidedCourseLight,
  chip: '#F0F2F7',
  // The design's gold and violet -- navy and blue are already primary and
  // secondary, so they are read straight off those rather than duplicated.
  brand: {
    gold: '#F5B300',
    // The offset a pressable gold button sits on. It has to read as a shadow,
    // so it is the design's own darker gold rather than --gold-soft, which is
    // close enough to the fill to look flat.
    goldDeep: '#D18F00',
    goldInk: '#8A6A12',
    onGold: '#1A2230',
    violet: '#5B34C7',
    amber: '#BA7517',
    teal: '#0F8A6A',
    iris: '#6D5AE6',
  },
};

const darkPaletteOptions: NonNullable<ThemeOptions['palette']> = {
  mode: 'dark',
  background: { default: '#1e2227', paper: '#282d34' },
  divider: '#3f454e',
  primary: { main: '#5B95E0', dark: '#4A82C8' },
  secondary: { main: '#6EA8E8' },
  text: { primary: '#e6e9ee', secondary: '#9aa2ad' },
  error: { main: '#FF5C74' },
  warning: { main: '#F5A64E' },
  info: { main: '#3FBBEE' },
  success: { main: '#55B95A' },
  action: { selected: alpha('#5B95E0', 0.1) },
  chip: '#333941',
  // Brand hues lifted for a dark ground: the light-mode gold and blue are
  // saturated enough to glare against a near-black surface.
  brand: {
    gold: '#E8B32E',
    goldDeep: '#C9932A',
    goldInk: '#F0D488',
    onGold: '#1A2230',
    violet: '#9B7BEA',
    amber: '#E0A24A',
    teal: '#3FBF9A',
    iris: '#9C8CF2',
  },
  guidedCourse: guidedCourseDark,
};

const typography: ThemeOptions['typography'] = {
  fontFamily: 'Poppins',
  fontSize: 14,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  htmlFontSize: 14,
  h1: {
    fontWeight: 700,
    fontSize: 50,
    lineHeight: 1.2,
    letterSpacing: '-1.5px',
    fontFamily: 'Poppins',
  },
  h2: {
    fontWeight: 600,
    fontSize: 44,
    lineHeight: 1.3,
    letterSpacing: '-0.01562em',
  },
  h3: {
    fontWeight: 600,
    fontSize: 36,
    lineHeight: 1.3,
    letterSpacing: '-0.01562em',
  },
  h4: {
    fontWeight: 600,
    fontSize: 23,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h5: {
    fontWeight: 500,
    fontSize: 20,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h6: {
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 1.5,
    letterSpacing: '0.35px',
  },
  subtitle1: {
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: '0.15px',
    lineHeight: 1.5,
  },
  subtitle2: {
    fontWeight: 400,
    fontSize: 14,
    letterSpacing: '0.15px',
    lineHeight: 1.5,
  },
  body1: { fontWeight: 500, fontSize: 14, letterSpacing: '0.15px' },
  body2: { fontWeight: 600, fontSize: 16, letterSpacing: '0.15px' },
  caption: { fontWeight: 400, fontSize: 12, letterSpacing: '0.15px' },
  overline: {
    fontWeight: 400,
    fontSize: 10,
    lineHeight: 2,
    letterSpacing: '0.15px',
    textTransform: 'none',
  },
  button: {
    fontWeight: 400,
    fontSize: 14,
    letterSpacing: '0.15px',
    paddingTop: '3px',
    paddingBottom: '3px',
  },
};

const shape: ThemeOptions['shape'] = { borderRadius: 4 };

const components: ThemeOptions['components'] = {
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        // MUI dark mode stamps a white-tint background-image overlay on Paper
        // to signal elevation. Our dark palette already encodes elevation via
        // distinct background tokens, so we suppress the overlay entirely.
        ...theme.applyStyles('dark', { backgroundImage: 'none' }),
      }),
      rounded: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 3,
        boxShadow: 'rgba(0,0,0,0.2) 0px 0px 6px 0px',
        // maxHeight / overflow belong on Menu paper only — see MuiMenu below.
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      }),
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        padding: '24px',
        [theme.breakpoints.down('md')]: {
          padding: '16px',
        },
        boxShadow: '0px 4px 6px rgba(128, 145, 196, 0.16)',
        ...theme.applyStyles('dark', {
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.4)',
        }),
      }),

      root: () => ({
        '&.MuiDrawer-anchorBottom .MuiDrawer-paper': {
          borderRadius: '24px 24px 0px 0px',
        },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: () => ({
        marginTop: '8px',
        maxHeight: '435px',
        overflow: 'auto',
      }),
      list: {
        padding: '0px',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiInputBase-input': {
          padding: '11.94px 12px',
          fontSize: '14px',
          fontWeight: '400',
          borderRadius: '12px',
          color: theme.palette.text.primary,
          '::-webkit-outer-spin-button,::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
            margin: '0px',
          },
        },
        // Textarea (multiline) shouldn't carry the single-line border-radius on
        // the element itself — the container (.MuiInputBase-root) handles that.
        '& .MuiInputBase-inputMultiline': {
          borderRadius: '0px',
        },
        '& .MuiInputBase-root': {
          borderRadius: '12px',
          backgroundColor: theme.palette.background.paper,
        },
        '& .MuiInputBase-input::placeholder': {
          opacity: 1,
          color: alpha(theme.palette.text.primary, 0.25),
          fontWeight: '400',
        },
        '& .Mui-disabled': {
          backgroundColor: '#F1F2F7',
          cursor: 'not-allowed',
          ...theme.applyStyles('dark', { backgroundColor: '#2A2F38' }),
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#D2D5DA',
          ...theme.applyStyles('dark', {
            borderColor: 'rgba(255, 255, 255, 0.23)',
          }),
        },
        '& .MuiInputBase-multiline': { padding: '0px' },
        '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: '#F1F2F7',
          ...theme.applyStyles('dark', { borderColor: '#2A2F38' }),
        },
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.error.main,
        fontWeight: 600,
        '&.MuiFormHelperText-root.Mui-error': {
          color: theme.palette.error.main,
          fontWeight: 600,
        },
      }),
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-checked': { color: theme.palette.primary.main },
      }),
    },
  },
  MuiMobileStepper: {
    styleOverrides: {
      root: {
        '&.MuiMobileStepper-dot': {
          width: '20px',
          height: '20px',
          margin: '0 15px',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      colorPrimary: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        '&:hover, &:focus, &:active': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        },
      }),
      colorSecondary: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.primary.main, 0.46),
        '&:hover, &:focus, &:active': {
          backgroundColor: alpha(theme.palette.primary.main, 0.46),
        },
      }),
      label: ({ theme, ownerState }) => ({
        fontSize: '12px',
        fontWeight: 600,
        // Only the default/untinted chip gets the branded blue label — chips
        // using a semantic color (success/warning/error/info) need MUI's own
        // contrast-aware text, otherwise e.g. a filled "success" chip gets
        // primary-blue text on a green background: two similarly bright
        // colors with poor contrast.
        ...(ownerState.color === 'default' && {
          color: theme.palette.primary.main,
        }),
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '& .indicator': {
          height: '4px',
          width: '15%',
          left: '5%',
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 3,
        fontFamily: 'Poppins',
        padding: '7.75px 16px',
        '&.MuiButton-contained': {
          textTransform: 'capitalize',
          boxShadow: 'none',
          fontWeight: 500,
          fontFamily: 'Poppins',
          '&.Mui-disabled': { opacity: 0.5 },
          '&:hover': {
            boxShadow: 'none',
          },
        },
        '&.MuiButton-outlined.MuiButton-sizeLarge': {
          padding: '9.2px 20px',
        },
        '&.MuiButton-contained.MuiButton-sizeLarge,&.MuiButton-text.MuiButton-sizeLarge':
          {
            padding: '10px 20px',
          },

        '&.MuiButton-outlined.MuiButton-sizeSmall': {
          padding: '4.7px 12px',
        },
        '&.MuiButton-contained.MuiButton-sizeSmall, &.MuiButton-text.MuiButton-sizeSmall':
          {
            padding: '5.5px 12px',
          },

        '&.MuiButton-outlined': {
          padding: '6.95px 16px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        '&.MuiButton-text': {
          textTransform: 'none',
          boxShadow: 'none',
          fontWeight: 500,
          '&:hover': {
            boxShadow: 'none',
          },
        },
      }),
      startIcon: { marginRight: 12 },
      sizeSmall: {
        fontSize: 12,
        '& .MuiSvgIcon-root': { fontSize: 20 },
      },
      sizeMedium: { fontSize: 14, '& .MuiSvgIcon-root': { fontSize: 24 } },
      sizeLarge: {
        fontSize: 16,
        '& .MuiSvgIcon-root': { fontSize: 28 },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { '&.Mui-disabled': { opacity: 0.5 } },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: '#F1F2F7',
        ...theme.applyStyles('dark', {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        }),
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: ({ theme }) => ({
        color: theme.palette.text.primary,
        fontWeight: '400',
        fontSize: '14px',
      }),
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '12px',
        '&.Mui-disabled': {
          backgroundColor: '#F1F2F7',
          cursor: 'not-allowed',
          ...theme.applyStyles('dark', { backgroundColor: '#2A2F38' }),
        },
        '&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: '#F1F2F7 !important',
          ...theme.applyStyles('dark', { borderColor: '#2A2F38 !important' }),
        },
      }),
      notchedOutline: ({ theme }) => ({
        borderColor: '#D2D5DA',
        ...theme.applyStyles('dark', {
          borderColor: 'rgba(255, 255, 255, 0.23)',
        }),
      }),
    },
  },
  MuiPickersSectionList: {
    styleOverrides: {
      root: {
        padding: '11.94px 0px !important',
      },
      sectionContent: ({ theme }) => ({
        fontSize: '14px',
        fontWeight: '400',
        color: theme.palette.text.primary,
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        boxShadow: 'rgba(0,0,0,0.2) 0px 0px 6px 0px',
        borderRadius: parseInt(theme.shape.borderRadius.toString(), 10) * 3,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        padding: parseInt(theme.shape.borderRadius.toString(), 10) * 3,
        ...theme.applyStyles('dark', {
          border: `1px solid ${theme.palette.divider}`,
        }),
      }),
      popper: ({ theme }) => ({
        [`& .MuiTooltip-arrow`]: {
          color: theme.palette.background.paper,
        },
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        padding: '12px',
        ...theme.applyStyles('dark', {
          border: `1px solid ${theme.palette.divider}`,
        }),
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '8px 24px 24px 24px',
      },
    },
  },
};

// Exported as ThemeOptions so ResumeBuilder can spread it into a sub-theme.
export const lightThemeOptions: ThemeOptions = {
  components,
  typography,
  shape,
  palette: lightPaletteOptions,
};

const lightTheme = createTheme({ breakpoints, ...lightThemeOptions });

export const darkTheme = createTheme({
  breakpoints,
  components,
  typography,
  shape,
  palette: darkPaletteOptions,
});

export default lightTheme;
